import { BUSINESS_HOURS, EMAIL, PHONE } from 'utils/constants';
import { Button } from 'components';
import { formatApplicationSummary } from '../../formatters/formatApplicationSummary';
import { formatStringOrLoading } from '../../formatters/formatNumberToLocale';
import { LockIcon } from 'components/icons';
import { RegistrationGlobalComponent } from 'types/components';
import { useApplicationSummary } from '../../hooks/useApplicationSummary';
import { useTranslation } from 'react-i18next';
import BusinessInfo from 'components/molecules/BusinessInfo/BusinessInfo';
import DesktopProductProperty from 'components/molecules/DesktopProductProperty/DesktopProductProperty';
import Divider from 'components/atoms/Divider/Divider';
import GTMWidgets from 'components/GTMWidgets/GTMWidgets';
import React from 'react';
import styles from 'components/Panel/Panel.module.scss';

export const Panel: RegistrationGlobalComponent = ({
  hideEditButton,
  onCalculatorToggle,
}) => {
  const { t } = useTranslation('summary');
  const summary = useApplicationSummary();
  const { amount, loading, term, interest, repayableAmount, repaymentDate } =
    formatApplicationSummary(summary);

  const content = {
    title: t('title'),
    amount: {
      value: formatStringOrLoading(loading, amount, '###'),
      unit: '€',
      label: t('amount'),
    },
    duration: {
      value: formatStringOrLoading(loading, term, '##'),
      unit: t('common:days'),
      label: t('duration'),
    },
    interest: {
      value: formatStringOrLoading(loading, interest, '##'),
      unit: '€',
      label: t('interest'),
    },
    repayableAmount: {
      value: formatStringOrLoading(loading, repayableAmount, '###'),
      unit: '€',
      label: t('repayable_amount'),
    },
    repaymentDate: {
      value: formatStringOrLoading(loading, repaymentDate, '##/##/####'),
      label: t('repayment_date'),
    },
    edit: t('edit'),
    secured: t('secured'),
    contact: {
      phone: PHONE,
      email: EMAIL,
      businessHours: BUSINESS_HOURS,
    },
    cookies: t('cookies'),
    accessibility: t('accessibility'),
  };

  return (
    <div className={styles.summary} data-test="panel-summary">
      <div className={styles.title} data-test="xpath-Title">
        {content.title}
      </div>
      <Divider />
      <DesktopProductProperty
        main
        loading={loading}
        value={content.amount.value}
        valueSuffix={content.amount.unit}
        label={content.amount.label}
      />
      <DesktopProductProperty
        main
        loading={loading}
        value={content.duration.value}
        valueSuffix={content.duration.unit}
        label={content.duration.label}
      />
      {hideEditButton ? (
        <Divider />
      ) : (
        <Button
          small
          outlined
          onClick={onCalculatorToggle}
          dataTest="css-Edit-Button"
          className={styles['edit-button']}
          loading={loading}
        >
          {content.edit}
        </Button>
      )}

      <DesktopProductProperty
        value={content.interest.value}
        valueSuffix={content.interest.unit}
        label={content.interest.label}
        loading={loading}
      />
      <DesktopProductProperty
        value={content.repayableAmount.value}
        valueSuffix={content.repayableAmount.unit}
        label={content.repayableAmount.label}
        loading={loading}
      />
      <DesktopProductProperty
        value={content.repaymentDate.value}
        label={content.repaymentDate.label}
        loading={loading}
      />
      <Divider />
      <div className={styles.secured} data-test="xpath-Trust-Secured">
        <LockIcon /> {content.secured}
      </div>
      <div className={styles.footer} data-test="panel-footer-business-wrapper">
        <BusinessInfo
          className={styles['mobile-footer']}
          businessHours={content.contact.businessHours}
          contactMail={content.contact.email}
          contactPhone={content.contact.phone}
        />
      </div>
      <GTMWidgets />
    </div>
  );
};
